import {
  createTheme,
  type PaletteOptions,
  type Theme,
  type ThemeOptions,
} from '@mui/material/styles';

import type {
  ColorblindMode,
  FontPreference,
  ThemeMode,
} from '@shared/profile.js';

/**
 * B19 theme-engine. The MUI theme is built dynamically from user preferences
 * (theme_mode + colorblind_mode + font_preference + reduced_motion). The
 * `ThemeController` rebuilds it live when settings change.
 */

export interface ThemePrefs {
  mode: ThemeMode;
  colorblind: ColorblindMode;
  font: FontPreference;
  reducedMotion: boolean;
  /** Optional admin custom theme (palette/typography overrides) merged last. */
  custom?: { palette?: Record<string, unknown>; typography?: Record<string, unknown> } | null;
}

const DEFAULT_PREFS: ThemePrefs = {
  mode: 'dark',
  colorblind: 'off',
  font: 'default',
  reducedMotion: false,
  custom: null,
};

const INTER_STACK = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const DYSLEXIA_STACK = 'OpenDyslexic, Inter, "Segoe UI", sans-serif';

/** Base palette per built-in variant. */
function variantPalette(mode: ThemeMode): PaletteOptions {
  switch (mode) {
    case 'light':
      return {
        mode: 'light',
        primary: { main: '#6d28d9' },
        secondary: { main: '#0891b2' },
        background: { default: '#fafafa', paper: '#ffffff' },
      };
    case 'high_contrast':
      return {
        mode: 'dark',
        primary: { main: '#ffffff' },
        secondary: { main: '#ffff00' },
        background: { default: '#000000', paper: '#000000' },
        text: { primary: '#ffffff', secondary: '#ffffff' },
      };
    case 'night':
      return {
        mode: 'dark',
        primary: { main: '#f59e0b' }, // warm
        secondary: { main: '#fb7185' },
        background: { default: '#0e0a06', paper: '#1a130b' },
      };
    case 'day':
      return {
        mode: 'light',
        primary: { main: '#2563eb' }, // cool
        secondary: { main: '#0ea5e9' },
        background: { default: '#f0f6ff', paper: '#ffffff' },
      };
    case 'dark':
    default:
      return {
        mode: 'dark',
        primary: { main: '#7c3aed' },
        secondary: { main: '#22d3ee' },
        background: { default: '#0b0b12', paper: '#15151f' },
      };
  }
}

/** Colorblind-safe accent overrides (applied on top of the variant palette). */
function colorblindOverrides(mode: ColorblindMode): Partial<Record<string, { main: string }>> {
  switch (mode) {
    case 'deuteranopia':
    case 'protanopia':
      // Avoid red/green confusion → blue/orange.
      return { primary: { main: '#0072B2' }, secondary: { main: '#E69F00' }, success: { main: '#56B4E9' }, error: { main: '#D55E00' } };
    case 'tritanopia':
      return { primary: { main: '#CC79A7' }, secondary: { main: '#009E73' }, error: { main: '#D55E00' } };
    case 'achromatopsia':
      return { primary: { main: '#000000' }, secondary: { main: '#888888' }, error: { main: '#444444' } };
    case 'off':
    default:
      return {};
  }
}

export function buildTheme(input?: Partial<ThemePrefs>): Theme {
  const prefs: ThemePrefs = { ...DEFAULT_PREFS, ...input };

  const palette: PaletteOptions = { ...variantPalette(prefs.mode), ...colorblindOverrides(prefs.colorblind) };

  const options: ThemeOptions = {
    palette,
    typography: {
      fontFamily: prefs.font === 'dyslexia' ? DYSLEXIA_STACK : INTER_STACK,
      h1: { fontSize: '2.5rem', fontWeight: 700, letterSpacing: '-0.02em' },
      h2: { fontSize: '2rem', fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    shape: { borderRadius: 12 },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: { root: { paddingInline: 24, paddingBlock: 12 } },
      },
    },
  };

  if (prefs.reducedMotion) {
    // Disable MUI transitions globally for vestibular-disorder users (WCAG 2.3.3).
    options.transitions = { create: () => 'none' };
    options.components = {
      ...options.components,
      MuiCssBaseline: {
        styleOverrides: {
          '*, *::before, *::after': {
            transition: 'none !important',
            animation: 'none !important',
          },
        },
      },
    };
  }

  if (prefs.custom) {
    if (prefs.custom.palette) {
      options.palette = { ...palette, ...prefs.custom.palette } as PaletteOptions;
    }
    if (prefs.custom.typography) {
      options.typography = { ...(options.typography as object), ...prefs.custom.typography } as NonNullable<ThemeOptions['typography']>;
    }
  }

  return createTheme(options);
}

/** Default theme (dark) — used before user preferences load. */
export const theme = buildTheme();
